import { useAnalytics } from '@ecosystem/newron-client-lib';
import { feedback } from '@ecosystem/newron-design-system';
import { ColumnConfig, SortingOptionsType, Table, TableBody } from '@components/design/migrated/Table';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ClearNegativeIcon from '@assets/clear-negative.svg';
import ClearPositiveIcon from '@assets/clear-positive.svg';
import SlightNegativePositiveIcon from '@assets/slight-negative-positive.svg';
import {
  ColumnHeaderDisplayNameForOR,
  ColumnHeaderForOR,
  DefaultSortingOptionsForOpportunityReview,
  OpportunityReviewBoardGroupingPgKpi,
  OpportunityReviewTableContent,
  SaleType,
  TableId,
} from '@models/OpportunityReview';
import usePersistedSortingOpportunityReview from '@utils/hook/sorting/UsePersistedSortingOpportunityReview';
import { ChartElementName, ChartElementType } from '@utils/page-analytics/model/ChartInteractionTracking';
import {
  TABLE_CLICKED_TRACKING_TITLE,
  tableClickTrackingFunction,
  TableClickTrackingParam,
} from '@utils/page-analytics/model/TableClickTracking';
import { FactRevenue, PageName, PageType } from '@utils/pageConfig/Types';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { getOpportunityReviewTableColumns } from './opportunity-review-table-columns/OpportunityReviewTableColumns';
import OpportunityReviewTableHead from './opportunity-review-table-head/OpportunityReviewTableHead';
import OpportunityReviewTableRow from './opportunity-review-table-row/OpportunityReviewTableRow';
import * as styles from './OpportunityReviewBoard.module.scss';
import { formatCurrencyLabelColumnConfigList } from '@utils/currencyUtils/CurrencyUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getGrandTotalValue } from '@utils/TotalUtils';
import { OpportunityReviewTableGroupRows } from '@components/opportunity-review-group/group-table/OpportunityReviewTableGroupRows';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import { useOpportunityReviewBoardGroupEventState } from '@components/opportunity-review-group/utils/OpportunityReviewBoardGroupContext';
import { IRootState } from '@state/reducers/Reducers';

interface Props {
  tableContent: OpportunityReviewTableContent[];
  selectedGrowthType: GrowthTypes;
  loadMore: boolean;
  selectedSaleType: SaleType;
  factRevenue: FactRevenue;
  volume: string;
  groupContent: OpportunityReviewBoardGroupingPgKpi[];
  context: GroupContext;
  doPreserveSelections: boolean;
  setDoPreserveSelections: (value: boolean) => void;
}

const OpportunityReviewBoard: React.FC<Props> = ({
  tableContent,
  selectedGrowthType,
  loadMore,
  selectedSaleType,
  factRevenue,
  volume,
  groupContent,
  context,
  doPreserveSelections,
  setDoPreserveSelections,
}) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const { unavailablePgs } = useSelector((state: IRootState) => state.availabilityPg);

  const CURRENT_TOTAL_NAME = getGrandTotalValue();
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const { setSelectedSegments, selectedSegments, clearSelectedSegments } = useOpportunityReviewBoardGroupEventState();
  const memoizedTableContent = React.useMemo(() => JSON.stringify(tableContent), [tableContent]);

  useEffect(() => {
    if (doPreserveSelections) {
      return;
    }
    updateSelectedSegments();
  }, [memoizedTableContent]);

  const updateSelectedSegments = () => {
    const segmentItems = tableContent.map((item) => item.segment);
    const itemsToKeepSelected = selectedSegments.filter((seg) => segmentItems.includes(seg));
    if (itemsToKeepSelected.length > 0) {
      let finalSegments = itemsToKeepSelected;
      if (unavailablePgs.length > 0) {
        finalSegments = finalSegments.filter((pg) => !unavailablePgs.includes(pg));
      }
      setSelectedSegments(finalSegments);
    } else {
      clearSelectedSegments();
    }
  };

  const renderTableBody = () => {
    const total = tableContent.find((product) => product.segment === CURRENT_TOTAL_NAME);

    const productGroups = tableContent.filter((product) => product.segment !== CURRENT_TOTAL_NAME);

    return (
      <>
        {total && (
          <OpportunityReviewTableHead
            opportunityReviewBoardPg={total}
            columns={columns}
            sortedColumn={sorting.headerKey}
            selectedGrowthType={selectedGrowthType}
            currencySymbol={factRevenue.currencySymbol}
          />
        )}
        <OpportunityReviewTableGroupRows
          opportunityReviewGroups={groupContent}
          currencySymbol={factRevenue.currencySymbol}
          columns={columns}
          sortedColumn={sorting.headerKey}
          selectedGrowthType={selectedGrowthType}
          context={context}
        />
        <TableBody>
          {productGroups.map((productGroup) => (
            <OpportunityReviewTableRow
              key={productGroup.segment}
              opportunityReviewBoardPg={productGroup}
              columns={columns}
              sortedColumn={sorting.headerKey}
              className={styles.OpportunityReviewBoard__TableRow__WithHover}
              selectedGrowthType={selectedGrowthType}
              currencySymbol={factRevenue.currencySymbol}
            />
          ))}
        </TableBody>
      </>
    );
  };

  const { sorting, updateOptions } = usePersistedSortingOpportunityReview({
    tableId: TableId,
    defaultSorting: DefaultSortingOptionsForOpportunityReview,
    selectedSaleType,
  });
  // Table Clicked Tracking Function
  const handleTableClickedTracking = (options: SortingOptionsType) => {
    //Get the Table Header columns again based on selectedSaleType
    const opportunityReviewTableHeader = getOpportunityReviewTableColumns(selectedSaleType, volume).find(
      (e) => e.headerKey === options.headerKey,
    );
    const trackingParam: TableClickTrackingParam = {
      eventTitle: TABLE_CLICKED_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.OPPORTUNITY_REVIEW,
      pageName: PageName.OPPORTUNITY_REVIEW,
      componentName: ChartElementName.TABLE_SORT,
      elementType: ChartElementType.SORT,
      optionChosen: `${options.label} ${_.capitalize(options.sortDir)}`,
      ...(opportunityReviewTableHeader && { column: `${opportunityReviewTableHeader.name}` }),
      access: pagesConfig.access,
    };
    tableClickTrackingFunction(trackingParam);
  };
  const updateSort = useCallback(
    (options: SortingOptionsType) => {
      handleTableClickedTracking(options);
      updateOptions(options);
      setDoPreserveSelections(true);
    },
    [updateOptions],
  );

  useEffect(() => {
    if (tableContent) {
      const cols: ColumnConfig[] = [
        { headerKey: 'checkbox', name: '', className: 'z-20' },
        {
          headerKey: ColumnHeaderForOR.categoryGroup,
          name: ColumnHeaderDisplayNameForOR()[ColumnHeaderForOR.categoryGroup],
          className: 'z-20',
        },
        ...getOpportunityReviewTableColumns(selectedSaleType, volume),
      ];
      setColumns(formatCurrencyLabelColumnConfigList(cols, factRevenue));
    }
  }, [tableContent, sorting]);

  return (
    <div data-testid="Opportunity-Review-Board" className={styles.OpportunityReviewBoard__Card}>
      <Table
        dontWrap={true}
        columns={columns}
        isHeaderSticky={true}
        sortingOptions={sorting}
        setSortingOptions={updateSort}
        defaultSortingOptions={sorting}
        className={styles.OpportunityReviewBoard__Table}
      >
        {renderTableBody()}
        {loadMore && (
          <tbody>
            <tr>
              <td colSpan={columns.length}>
                <feedback.DotsLoader />
              </td>
            </tr>
          </tbody>
        )}
      </Table>
      <div className={styles.OpportunityReviewBoard__TableCell_Legend}>
        <img
          src={ClearNegativeIcon}
          alt="legend-clear-negative"
          className={styles.OpportunityReviewBoard__TableCell_LegendIcon}
        />
        {'Clear Negative'}
        <img
          src={SlightNegativePositiveIcon}
          alt="legend-slight"
          className={styles.OpportunityReviewBoard__TableCell_LegendIcon}
        />
        {'Slight Positive / Negative'}
        <img
          src={ClearPositiveIcon}
          alt="legend-clear-positive"
          className={styles.OpportunityReviewBoard__TableCell_LegendIcon}
        />
        {'Clear Positive'}
      </div>
    </div>
  );
};

export default OpportunityReviewBoard;
